/**
 * Input/Output Sanitization Utilities
 * 
 * Provides XSS protection using DOMPurify for all user-generated content.
 * CRITICAL: Always sanitize before rendering any user input in the UI.
 * 
 * Defense in Depth:
 * - DOMPurify for HTML sanitization
 * - Strict allowlists for tags and attributes
 * - URL protocol validation
 * - Null byte and control character stripping
 */

import DOMPurify from 'dompurify'

// Ensure DOMPurify is initialized with window (browser environment)
// This prevents issues when DOMPurify is imported before DOM is ready
const purify = typeof window !== 'undefined' ? DOMPurify : null

// Configure DOMPurify hooks for additional security
if (purify) {
  // Remove any data: URLs from src attributes (images can be malicious)
  purify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName === 'src' || data.attrName === 'href') {
      const value = data.attrValue.toLowerCase().trim()
      if (value.startsWith('data:') && !value.startsWith('data:image/')) {
        data.attrValue = ''
      }
      if (value.startsWith('javascript:') || value.startsWith('vbscript:')) {
        data.attrValue = ''
      }
    }
  })
  
  // Log any removed elements in development
  if (import.meta.env.DEV) {
    purify.addHook('uponSanitizeElement', (_node, data) => {
      if (data.tagName && !data.allowedTags?.[data.tagName]) {
        // Removed in favor of secure logger approach
      }
    })
  }
}

/**
 * Strip null bytes and control characters
 * These can be used to bypass security filters
 */
function stripControlChars(str: string): string {
  // eslint-disable-next-line no-control-regex
  return str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
}

/**
 * Fallback text escaping for when DOMPurify is not available
 * Basic HTML entity encoding
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

/**
 * Sanitize HTML content to prevent XSS attacks
 * Use this for any HTML content from users or external sources
 * 
 * @param dirty - Untrusted HTML string
 * @returns Safe HTML string with dangerous content removed
 * 
 * @example
 * const userInput = '<script>alert("XSS")</script><p>Hello</p>'
 * const safe = sanitizeHtml(userInput) // Returns: '<p>Hello</p>'
 */
export function sanitizeHtml(dirty: string): string {
  // Handle empty or non-string inputs
  if (!dirty || typeof dirty !== 'string') {
    return ''
  }
  
  // Strip control characters first
  const cleaned = stripControlChars(dirty)
  
  if (!purify) {
    // Fallback: just escape HTML entities (development SSR case)
    return escapeHtml(cleaned)
  }
  
  try {
    return purify.sanitize(cleaned, {
      ALLOWED_TAGS: [
        'b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre',
        'span', 'div' // Added for styling purposes
      ],
      ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'class'],
      ALLOW_DATA_ATTR: false,
      FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'form', 'input'],
      FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick', 'onmouseover'],
      // Force all links to be safe
      ADD_ATTR: ['target', 'rel'],
      FORCE_BODY: true,
    })
  } catch {
    return escapeHtml(cleaned)
  }
}

/**
 * Sanitize plain text - strips all HTML tags
 * Use this for text-only fields like usernames, display names, etc.
 * 
 * @param dirty - Untrusted text that might contain HTML
 * @returns Plain text with all HTML removed
 * 
 * @example
 * const userInput = '<script>alert("XSS")</script>John Doe'
 * const safe = sanitizeText(userInput) // Returns: 'John Doe'
 */
export function sanitizeText(dirty: string): string {
  // Handle empty or non-string inputs
  if (!dirty || typeof dirty !== 'string') {
    return ''
  }
  
  // Strip control characters first
  const cleaned = stripControlChars(dirty)
  
  if (!purify) {
    // Fallback: just escape HTML entities
    return escapeHtml(cleaned)
  }
  
  try {
    return purify.sanitize(cleaned, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true // Keep text content, remove tags
    })
  } catch {
    return escapeHtml(cleaned)
  }
}

/**
 * Sanitize for attribute values (IDs, class names, etc.)
 * Removes any dangerous characters that could break out of attributes
 * 
 * @param dirty - Untrusted attribute value
 * @returns Safe attribute value
 */
export function sanitizeAttribute(dirty: string): string {
  // Remove any quotes, brackets, or script-like content
  return dirty.replace(/[<>"'`=]/g, '')
}

/**
 * Sanitize URL to prevent javascript: and data: protocols
 * 
 * @param url - Untrusted URL
 * @returns Safe URL or empty string if dangerous
 */
export function sanitizeUrl(url: string): string {
  if (!purify) {
    // Fallback: basic protocol check
    if (url.match(/^(https?:\/\/|mailto:)/i) || !url.match(/^[a-zA-Z]+:/)) {
      return url.replace(/[<>"'`]/g, '')
    }
    return ''
  }
  
  const cleaned = purify.sanitize(url, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: []
  })
  
  // Only allow http, https, and mailto protocols
  if (cleaned.match(/^(https?:\/\/|mailto:)/i)) {
    return cleaned
  }
  
  // If no protocol, assume it's a relative URL
  if (!cleaned.match(/^[a-zA-Z]+:/)) {
    return cleaned
  }
  
  // Dangerous protocol detected
  return ''
}

/**
 * Sanitize user object fields
 * Apply appropriate sanitization to common user fields
 * 
 * @param user - User object with potentially untrusted data
 * @returns Sanitized user object
 */
export function sanitizeUser<T extends Record<string, any>>(user: T): T {
  if (!user) return user
  
  const sanitized: any = { ...user }
  
  // Sanitize string fields if they exist
  if (typeof sanitized.displayName === 'string') {
    sanitized.displayName = sanitizeText(sanitized.displayName)
  }
  if (typeof sanitized.email === 'string') {
    sanitized.email = sanitizeText(sanitized.email)
  }
  if (typeof sanitized.firstName === 'string') {
    sanitized.firstName = sanitizeText(sanitized.firstName)
  }
  if (typeof sanitized.lastName === 'string') {
    sanitized.lastName = sanitizeText(sanitized.lastName)
  }
  if (typeof sanitized.organizationName === 'string') {
    sanitized.organizationName = sanitizeText(sanitized.organizationName)
  }
  if (typeof sanitized.subscriptionTier === 'string') {
    sanitized.subscriptionTier = sanitizeText(sanitized.subscriptionTier)
  }
  if (sanitized.dashboardPreferences && typeof sanitized.dashboardPreferences === 'object') {
    sanitized.dashboardPreferences = { ...sanitized.dashboardPreferences }
  }
  
  return sanitized as T
}

/**
 * Sanitize form data before submission
 * Recursively sanitize all string values in an object
 * 
 * @param data - Form data object
 * @returns Sanitized form data
 */
export function sanitizeFormData<T extends Record<string, any>>(data: T): T {
  const sanitized: any = {}
  
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeText(value)
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map(item => 
        typeof item === 'string' ? sanitizeText(item) : item
      )
    } else if (value && typeof value === 'object') {
      sanitized[key] = sanitizeFormData(value)
    } else {
      sanitized[key] = value
    }
  }
  
  return sanitized
}

/**
 * Create a React-safe HTML rendering helper
 * Use this with dangerouslySetInnerHTML (which becomes safe with this)
 * 
 * @param html - HTML content to sanitize
 * @returns Object for dangerouslySetInnerHTML prop
 * 
 * @example
 * <div dangerouslySetInnerHTML={createSafeHtml(userContent)} />
 */
export function createSafeHtml(html: string) {
  return {
    __html: sanitizeHtml(html)
  }
}
